import { useState, createContext, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Search,
  History,
  Building2,
  Users,
  Zap,
  Mail,
  Send,
  Globe,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Target,
  Settings,
  LayoutDashboard,
} from "lucide-react";

// Create context for sidebar state
const SidebarContext = createContext({ collapsed: false, setCollapsed: () => {} });

export const useSidebar = () => useContext(SidebarContext);

const navigation = [
  {
    title: "Analyze",
    items: [
      { name: "New Analysis", href: "/", icon: Search },
      { name: "History", href: "/history", icon: History },
    ],
  },
  {
    title: "Leads",
    items: [
      { name: "Companies", href: "/companies", icon: Building2 },
      { name: "Lead Management", href: "/leads", icon: Users },
      { name: "Clay Pipeline", href: "/clay", icon: Zap },
    ],
  },
  {
    title: "Outreach",
    items: [
      { name: "Email Campaigns", href: "/email-campaigns", icon: Mail },
      { name: "Email History", href: "/email-history", icon: Send },
    ],
  },
  {
    title: "Tools",
    items: [
      { name: "Public Score Tool", href: "/score", icon: Globe },
    ],
  },
];

export function Sidebar({ collapsed, setCollapsed }) {
  const location = useLocation();

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-slate-900 border-r border-slate-800 transition-all duration-300 flex flex-col",
        collapsed ? "w-16" : "w-64"
      )}
      data-testid="sidebar"
      data-collapsed={collapsed}
    >
      {/* Logo Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-semibold text-white text-sm">Revenue Acceleration</span>
              <span className="text-emerald-400 text-xs block -mt-1">Score</span>
            </div>
          </Link>
        )}
        {collapsed && (
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto">
            <Target className="w-5 h-5 text-white" />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {navigation.map((group) => (
          <div key={group.title} className="mb-6">
            {!collapsed && (
              <h3 className="px-3 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {group.title}
              </h3>
            )}
            <ul className="space-y-1">
              {group.items.map((item) => {
                const isActive = location.pathname === item.href;
                const Icon = item.icon;
                
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                        isActive
                          ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                          : "text-slate-400 hover:text-white hover:bg-slate-800",
                        collapsed && "justify-center px-2"
                      )}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      title={collapsed ? item.name : undefined}
                    >
                      <Icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-emerald-400")} />
                      {!collapsed && <span>{item.name}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Collapse Toggle */}
      <div className="p-3 border-t border-slate-800">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            "w-full text-slate-400 hover:text-white hover:bg-slate-800",
            collapsed && "px-2"
          )}
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5 mr-2" />
              <span>Collapse</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
}

export function SidebarLayout({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="pl-64 transition-all duration-300" data-testid="main-content">
        {children}
      </main>
    </div>
  );
}
